/**
 * データセットの評価・再評価に関するユーティリティ関数
 */

import { DerivedDataSet, DataSet, DatasetId, ColumnId, DataValue } from '../../types';
import { executeOperation } from './executeOperation';
import logger from './logger';

/**
 * DerivedDataSetを再評価してdataキャッシュを生成
 *
 * DerivedDataSetはoperationを保持しているが、dataプロパティはオプショナル（キャッシュ）。
 * この関数はoperationを実行してdataを生成し、キャッシュ済みのDerivedDataSetを返す。
 *
 * @param derivedDataset - 再評価対象のDerivedDataSet
 * @param datasets - 親データセットを含むすべてのデータセット（evaluation context）
 * @returns dataキャッシュを持つDerivedDataSet
 *
 * @example
 * ```typescript
 * const derivedDataset = datasets['derived_123'];
 * if (!hasData(derivedDataset)) {
 *   const materialized = await evaluateDerivedDataset(derivedDataset, datasets);
 *   const accessor = new DatasetAccessor(materialized);
 * }
 * ```
 */
export async function evaluateDerivedDataset(
  derivedDataset: DerivedDataSet,
  datasets: Record<DatasetId, DataSet>
): Promise<DerivedDataSet & { data: Record<ColumnId, DataValue[]> }> {
  logger.debug('[evaluateDataset] Evaluating DerivedDataSet', {
    datasetId: derivedDataset.id,
    operationType: derivedDataset.operation.type
  });

  // operationを実行（derivedDatasetを渡してexclusionsを適用）
  const result = await executeOperation(
    derivedDataset.operation,
    datasets,
    derivedDataset.parentDatasetIds[0], // targetDatasetId（最初の親データセット）
    derivedDataset.schema, // existingSchema（列IDを保持するため）
    derivedDataset // exclusionsを適用するため
  );

  logger.debug('[evaluateDataset] Evaluation completed', {
    datasetId: derivedDataset.id,
    rowCount: result.rowCount
  });

  // dataとrowCountを追加したDerivedDataSetを返す
  return {
    ...derivedDataset,
    data: result.data ?? {},
    rowCount: result.rowCount ?? 0
  };
}
